import React, { useState } from 'react';
import { Scan, CheckCircle2, XCircle, AlertTriangle, Loader2, ClipboardPaste, X, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

import api from '../utils/api';

const STATUS_CONFIG = {
  created: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Created' },
  skipped: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Skipped' },
  failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Failed' }
};

export default function BulkVINScanner({ onClose, onImportComplete }) {
  const [vinText, setVinText] = useState('');
  const [condition, setCondition] = useState('good');
  const [autoPrice, setAutoPrice] = useState(true);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);

  const parseVINs = (text) => {
    return text
      .split(/[\n,;\s]+/)
      .map(v => v.trim().replace(/[^A-Za-z0-9]/g, ''))
      .filter(v => v.length === 17);
  };

  const vinCount = parseVINs(vinText).length;

  const handleImport = async () => {
    const vins = parseVINs(vinText);
    if (vins.length === 0) {
      toast.error('No valid 17-character VINs found');
      return;
    }
    if (vins.length > 50) {
      toast.error('Maximum 50 VINs per batch');
      return;
    }

    setImporting(true);
    setResults(null);
    setSummary(null);

    try {
      const { data } = await api.post('/api/vehicles/bulk-vin', {
        vins,
        default_condition: condition,
        default_status: 'available',
        auto_price: autoPrice
      });
      setResults(data.results);
      setSummary(data);
      if (data.created > 0) {
        toast.success(`${data.created} vehicle(s) imported${autoPrice ? ' with pricing' : ''}!`);
        onImportComplete?.();
      } else {
        toast.warning('No new vehicles were imported');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setVinText(text);
      toast.success('Pasted from clipboard');
    } catch {
      toast.error('Could not read clipboard');
    }
  };

  const formatPrice = (n) => n ? `$${Number(n).toLocaleString()}` : '--';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="bulk-vin-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 flex items-center justify-center">
              <Scan className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Bulk VIN Scanner</h2>
              <p className="text-sm text-gray-500">Paste VINs to auto-populate inventory with pricing</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full" data-testid="bulk-vin-close">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {!results ? (
            <>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Paste VINs (one per line, comma, or space separated)</label>
                  <Button variant="ghost" size="sm" onClick={handlePaste} className="text-indigo-600 hover:text-indigo-700" data-testid="paste-clipboard-btn">
                    <ClipboardPaste className="w-4 h-4 mr-1" /> Paste
                  </Button>
                </div>
                <Textarea
                  value={vinText}
                  onChange={(e) => setVinText(e.target.value)}
                  placeholder={"1HGBH41JXMN109186\n5YFBURHE5FP326845\nWBA3A5C55CF256651\n\nPaste up to 50 VINs..."}
                  className="min-h-[160px] font-mono text-sm rounded-xl border-gray-200 focus:border-indigo-500 focus:ring-indigo-500"
                  data-testid="bulk-vin-textarea"
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-400">
                    {vinCount > 0 ? (
                      <span className="text-indigo-600 font-medium">{vinCount} valid VIN{vinCount !== 1 ? 's' : ''} detected</span>
                    ) : 'Paste VINs above to begin'}
                  </p>
                  {vinCount > 50 && <p className="text-xs text-red-500 font-medium">Max 50 per batch</p>}
                </div>
              </div>

              {/* Options Row */}
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Default Condition</label>
                  <Select value={condition} onValueChange={setCondition}>
                    <SelectTrigger className="rounded-xl" data-testid="bulk-vin-condition">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button
                  type="button"
                  onClick={() => setAutoPrice(!autoPrice)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                    autoPrice
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-gray-50 border-gray-200 text-gray-500'
                  }`}
                  data-testid="auto-price-toggle"
                >
                  <DollarSign className="w-4 h-4" />
                  AI Auto-Price {autoPrice ? 'ON' : 'OFF'}
                </button>
              </div>

              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
                <p className="text-sm text-indigo-700 font-medium mb-1">How it works</p>
                <ul className="text-xs text-indigo-600 space-y-1">
                  <li>1. Each VIN is decoded via the NHTSA database (year, make, model, engine, etc.)</li>
                  {autoPrice && <li>2. AI estimates fair market value based on vehicle specs & condition</li>}
                  <li>{autoPrice ? '3' : '2'}. Vehicles are created in your inventory with status "Available"</li>
                  <li>{autoPrice ? '4' : '3'}. Duplicate VINs already in your inventory are automatically skipped</li>
                </ul>
              </div>
            </>
          ) : (
            <>
              {/* Summary */}
              {summary && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-700">{summary.created}</p>
                    <p className="text-xs text-emerald-600">Created</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-amber-700">{summary.skipped}</p>
                    <p className="text-xs text-amber-600">Skipped</p>
                  </div>
                  <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-red-700">{summary.failed}</p>
                    <p className="text-xs text-red-600">Failed</p>
                  </div>
                </div>
              )}

              {/* Results List */}
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {results.map((r, i) => {
                  const cfg = STATUS_CONFIG[r.status];
                  const Icon = cfg.icon;
                  return (
                    <div key={i} className={`p-3 rounded-xl ${cfg.bg} border border-gray-100`} data-testid={`bulk-result-${i}`}>
                      <div className="flex items-center gap-3">
                        <Icon className={`w-5 h-5 flex-shrink-0 ${cfg.color}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono text-gray-600">{r.vin}</code>
                            <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                          </div>
                          {r.status === 'created' && (
                            <p className="text-sm text-gray-700 font-medium truncate">
                              {r.year} {r.make} {r.model} {r.body_type ? `- ${r.body_type}` : ''}
                            </p>
                          )}
                          {r.reason && <p className="text-xs text-gray-500">{r.reason}</p>}
                        </div>
                        {r.pricing && (
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-emerald-700">{formatPrice(r.pricing.suggested_price)}</p>
                            <p className="text-xs text-gray-400">
                              {formatPrice(r.pricing.price_range_low)} - {formatPrice(r.pricing.price_range_high)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex items-center justify-end gap-3">
          {!results ? (
            <>
              <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="bulk-vin-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={vinCount === 0 || vinCount > 50 || importing}
                className="bg-gradient-to-r from-indigo-500 to-violet-500 hover:opacity-90 text-white rounded-xl shadow-lg shadow-indigo-500/25"
                data-testid="bulk-vin-import-btn"
              >
                {importing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning {vinCount} VINs{autoPrice ? ' + pricing' : ''}...</>
                ) : (
                  <><Scan className="w-4 h-4 mr-2" /> Import {vinCount} VIN{vinCount !== 1 ? 's' : ''}{autoPrice ? ' with Pricing' : ''}</>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => { setResults(null); setSummary(null); setVinText(''); }} className="rounded-xl" data-testid="bulk-vin-new-batch">
                New Batch
              </Button>
              <Button onClick={onClose} className="bg-gradient-to-r from-indigo-500 to-violet-500 hover:opacity-90 text-white rounded-xl" data-testid="bulk-vin-done">
                Done
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
